import { apiRequest } from '../../../shared/api';
import {
  Occurrence,
  OccurrenceCategory,
  OccurrenceType,
  DashboardStats,
  OccurrenceStatus,
  OccurrenceSeverity,
} from './types';

export interface OccurrenceListResponse {
  items: Occurrence[];
  total: number;
}

export const occurrenceService = {
  // Categories & Types
  getCategories: () => apiRequest<OccurrenceCategory[]>('/occurrences/meta/categories'),

  getOccurrenceTypes: (categoryId?: string) =>
    apiRequest<OccurrenceType[]>(`/occurrences/meta/types${categoryId ? `?categoryId=${categoryId}` : ''}`),

  createOccurrenceType: (data: {
    categoryId: string;
    code: string;
    name: string;
    icaoCode?: string;
    isMandatoryDefault?: boolean;
  }) =>
    apiRequest<OccurrenceType>('/occurrences/meta/types', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Dashboard KPI
  getDashboardStats: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<DashboardStats>(`/occurrences/analytics/dashboard?organizationId=${organizationId}`),

  // List occurrences
  getOccurrences: (params: {
    organizationId?: string;
    status?: OccurrenceStatus | OccurrenceStatus[];
    severity?: OccurrenceSeverity;
    categoryId?: string;
    typeId?: string;
    isMandatory?: boolean;
    searchTerm?: string;
    forLinkage?: boolean;
    scope?: string;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.organizationId) query.append('organizationId', params.organizationId);
    if (params.status) {
      if (Array.isArray(params.status)) {
        params.status.forEach(s => query.append('status', s));
      } else {
        query.append('status', params.status);
      }
    }
    if (params.severity) query.append('severity', params.severity);
    if (params.categoryId) query.append('categoryId', params.categoryId);
    if (params.typeId) query.append('typeId', params.typeId);
    if (params.isMandatory !== undefined) query.append('isMandatory', String(params.isMandatory));
    if (params.searchTerm) query.append('searchTerm', params.searchTerm);
    if (params.forLinkage) query.append('for_linkage', 'true');
    if (params.scope) query.append('scope', params.scope);
    if (params.limit) query.append('limit', String(params.limit));
    if (params.offset) query.append('offset', String(params.offset));

    return apiRequest<OccurrenceListResponse>(`/occurrences?${query.toString()}`);
  },

  // Get Detail
  getOccurrenceById: (id: string) => apiRequest<Occurrence>(`/occurrences/${id}`),

  // Create
  createOccurrence: (data: Partial<Occurrence>) =>
    apiRequest<Occurrence>('/occurrences', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Update
  updateOccurrence: (id: string, data: Partial<Occurrence>) =>
    apiRequest<Occurrence>(`/occurrences/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Workflow actions
  verifyOccurrence: (id: string, reason: string) =>
    apiRequest<Occurrence>(`/occurrences/${id}/verify`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  triggerInvestigation: (
    id: string,
    reason: string,
    leadInvestigator?: { id: string; name: string; department: string }
  ) =>
    apiRequest<Occurrence>(`/occurrences/${id}/trigger-investigation`, {
      method: 'POST',
      body: JSON.stringify({
        reason,
        assignToUserId: leadInvestigator?.id,
        assignToName: leadInvestigator?.name,
        department: leadInvestigator?.department,
      }),
    }),

  closeOccurrence: (id: string, reason: string) =>
    apiRequest<Occurrence>(`/occurrences/${id}/close`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  reopenOccurrence: (id: string, reason: string) =>
    apiRequest<Occurrence>(`/occurrences/${id}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // Report linking
  linkReport: (id: string, reportId: string, reportNumber: string, isPrimary: boolean = false, notes?: string) =>
    apiRequest<any>(`/occurrences/${id}/reports`, {
      method: 'POST',
      body: JSON.stringify({ reportId, reportNumber, isPrimary, notes }),
    }),

  unlinkReport: (id: string, reportId: string) =>
    apiRequest<any>(`/occurrences/${id}/reports/${reportId}`, {
      method: 'DELETE',
    }),

  // Assignments
  assignInvestigator: (
    id: string,
    assignedToUserId: string,
    assignedToName: string,
    department: string,
    role: string,
    notes?: string
  ) =>
    apiRequest<any>(`/occurrences/${id}/assignments`, {
      method: 'POST',
      body: JSON.stringify({ assignedToUserId, assignedToName, department, role, notes }),
    }),

  // Evidence
  addEvidence: (
    id: string,
    fileName: string,
    fileSize: number,
    mimeType: string,
    storageKey: string,
    confidentialityLevel: string = 'INTERNAL'
  ) =>
    apiRequest<any>(`/occurrences/${id}/evidence`, {
      method: 'POST',
      body: JSON.stringify({ fileName, fileSize, mimeType, storageKey, confidentialityLevel }),
    }),

  // Comments
  addComment: (id: string, commentText: string, isInternalOnly: boolean = true) =>
    apiRequest<any>(`/occurrences/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ commentText, isInternalOnly }),
    }),

  // ECCAIRS Export XML / JSON
  getEccairsExport: (id: string, format: 'json' | 'xml' = 'json') =>
    apiRequest<any>(`/occurrences/${id}/eccairs?format=${format}`),
};
